/**
 * Luật thuần của màn Phân quyền dữ liệu — port `views/DataPermView.tsx` và `components/QuyenKhuonTab.tsx` của native.
 *
 * Lưu cột BỊ CẤM, không lưu cột được xem: cột mới đồng bộ về mặc định hiện ra, thay vì bị giấu im lặng với mọi người.
 */
import type { KhuonGrant, PermGrant, PermLevel, PermTable } from '../types';

export interface VaiQuyen {
  role: string;
  label: string;
  /** Vai sửa được ma trận (đứng ngoài ma trận). */
  canManage: boolean;
}

/**
 * Gộp danh sách vai: `PermAccounts.accounts` của module đi trước (giữ `label`, `canManage` máy chủ tính), rồi vai của tổ
 * chức (API vai trò platform) mà module chưa liệt kê — module không tự liệt kê được vai. Vai thêm vào chưa có dòng nào
 * trong ma trận, nên hiện ở mức đóng (`none`) cho tới khi có người cấp.
 */
export function gopDanhSachVai(
  accounts: readonly VaiQuyen[] | undefined,
  vaiToChuc: readonly { ma: string; ten: string }[] | undefined,
): VaiQuyen[] {
  const ra: VaiQuyen[] = [...(accounts ?? [])];
  const co = new Set(ra.map((a) => a.role.toUpperCase()));
  for (const v of vaiToChuc ?? []) {
    if (co.has(v.ma.toUpperCase())) continue;
    co.add(v.ma.toUpperCase());
    ra.push({ role: v.ma, label: v.ten || v.ma, canManage: false });
  }
  return ra;
}

export type BanNhapQuyen = Record<string, PermGrant>;

export const dongRong = (role: string): PermGrant => ({ role, level: 'none', tables: [] });

/** Đặt mức cho một vai, giữ danh sách bảng đã cấp (đổi qua lại mức không làm mất công tích từng bảng). */
export function datMuc(nhap: BanNhapQuyen, vai: string, level: PermLevel): BanNhapQuyen {
  return { ...nhap, [vai]: { role: vai, level, tables: nhap[vai]?.tables ?? [] } };
}

/** Bật/tắt đọc một bảng; bật một bảng là chuyển vai về mức `tables`. */
export function batBang(nhap: BanNhapQuyen, vai: string, table: string, on: boolean): BanNhapQuyen {
  const g = nhap[vai] ?? { role: vai, level: 'tables' as PermLevel, tables: [] };
  const ds = on
    ? [...g.tables.filter((t) => t.table !== table), { table, deniedColumns: [] }]
    : g.tables.filter((t) => t.table !== table);
  return { ...nhap, [vai]: { ...g, level: 'tables', tables: ds } };
}

/** Ẩn/hiện một cột của một bảng đã cấp. */
export function camCot(nhap: BanNhapQuyen, vai: string, table: string, col: string, cam: boolean): BanNhapQuyen {
  const g = nhap[vai];
  if (!g) return nhap;
  return {
    ...nhap,
    [vai]: {
      ...g,
      tables: g.tables.map((t) =>
        t.table !== table
          ? t
          : { ...t, deniedColumns: cam ? [...t.deniedColumns, col] : t.deniedColumns.filter((c) => c !== col) },
      ),
    },
  };
}

export type LocBang = 'tat_ca' | 'da_mo' | 'chua_mo';
export const LOC_BANG: LocBang[] = ['tat_ca', 'da_mo', 'chua_mo'];

/** Bỏ dấu để gõ "hoa don" vẫn ra "Hoá đơn". */
export function boDauThuong(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase();
}

export function locDanhSachBang(
  tables: readonly PermTable[],
  tuKhoa: string,
  loc: LocBang,
  daMo: ReadonlySet<string>,
): PermTable[] {
  const k = boDauThuong(tuKhoa.trim());
  return tables.filter((b) => {
    if (loc === 'da_mo' && !daMo.has(b.name)) return false;
    if (loc === 'chua_mo' && daMo.has(b.name)) return false;
    return !k || boDauThuong(b.name).includes(k) || boDauThuong(b.title).includes(k);
  });
}

/** Tóm tắt một dòng quyền thành mã + số để component dịch. */
export type TomTatQuyen =
  | { ma: 'none' }
  | { ma: 'full' }
  | { ma: 'chuaMo' }
  | { ma: 'bang'; n: number }
  | { ma: 'bangGiau'; n: number; c: number };

export function tomTatQuyen(g?: PermGrant): TomTatQuyen {
  if (!g || g.level === 'none') return { ma: 'none' };
  if (g.level === 'full') return { ma: 'full' };
  const n = g.tables.length;
  const c = g.tables.reduce((s, x) => s + x.deniedColumns.length, 0);
  if (!n) return { ma: 'chuaMo' };
  return c ? { ma: 'bangGiau', n, c } : { ma: 'bang', n };
}

/** Bản nháp khác bản gốc máy chủ không — so theo vai, bỏ dòng đóng mới thêm (chưa từng có trên máy chủ). */
export function coThayDoi<T extends { role: string; level: string }>(
  nhap: Record<string, T> | null,
  goc: readonly T[] | undefined,
): boolean {
  if (!nhap) return false;
  const g = goc ?? [];
  const coGoc = new Set(g.map((x) => x.role));
  const ds = Object.values(nhap).filter((x) => coGoc.has(x.role) || x.level !== 'none');
  const theoVai = (xs: readonly T[]) => [...xs].sort((a, b) => a.role.localeCompare(b.role));
  return JSON.stringify(theoVai(ds)) !== JSON.stringify(theoVai(g));
}

// ── Ma trận khuôn ────────────────────────────────────────────────────────────

export const dongKhuonRong = (role: string): KhuonGrant => ({ role, level: 'none', nhom: [], khuon: [], chan: [] });

/** Bỏ/thêm một mục trong danh sách, không nhân bản. */
export function dao(ds: readonly string[], x: string): string[] {
  return ds.includes(x) ? ds.filter((y) => y !== x) : [...ds, x];
}

/** Ở `full` mọi khuôn đã được cấp; ở `chon` là cả nhóm hoặc cấp riêng. `chan` thắng cả hai. */
export function duocDungKhuon(g: KhuonGrant, nhomId: string, khuonId: string): boolean {
  if (g.level === 'none' || g.chan.includes(khuonId)) return false;
  return g.level === 'full' || g.nhom.includes(nhomId) || g.khuon.includes(khuonId);
}

export type TomTatKhuon =
  | { ma: 'none' }
  | { ma: 'full' }
  | { ma: 'chan'; n: number }
  | { ma: 'chon'; nhom: number; khuon: number };

export function tomTatKhuon(g?: KhuonGrant): TomTatKhuon {
  if (!g || g.level === 'none') return { ma: 'none' };
  if (g.level === 'full') return g.chan.length ? { ma: 'chan', n: g.chan.length } : { ma: 'full' };
  return { ma: 'chon', nhom: g.nhom.length, khuon: g.khuon.length };
}
